import { Box, Paper, Stack, Typography } from "@mui/material";
import { Fragment } from "react";
import { ChannelIcon } from "../../channels/components/channel-icon";
import { sendTimeText } from "../program-timeline";
import { FolderName, SegmentName, StatusChip, TitleLink, isChannel } from "./timeline-parts";

import type { TimelineRow } from "../program-timeline";
import type { FC, ReactNode } from "react";

/**
 * One Communication: its channel, title, segment, folder and send time, its status, and its actions.
 * The title opens it, and its link covers the card.
 */
export const CommunicationCard: FC<{
  row: TimelineRow;
  folder: string | null;
  actions?: ReactNode;
}> = ({ row, folder, actions }) => {
  const parts: { key: string; node: ReactNode }[] = [];
  if (row.segment_name)
    parts.push({
      key: "segment",
      node: <SegmentName name={row.segment_name} fontSize={16} />,
    });
  if (folder) parts.push({ key: "folder", node: <FolderName name={folder} fontSize={14} /> });
  if (row.send_at) parts.push({ key: "time", node: sendTimeText(row.send_at) });

  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        pl: 1.75,
        pr: 1.5,
        borderRadius: "8px",
        boxShadow: "0 1px 2px rgba(100, 116, 139, 0.06)",
      }}
    >
      <ChannelIcon
        channel={isChannel(row.channel) ? row.channel : null}
        sx={{ fontSize: 28, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: 15, lineHeight: "22px" }}>
          <TitleLink row={row} covers />
        </Typography>
        <Stack
          direction="row"
          useFlexGap
          sx={{
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 0.75,
            fontSize: 13,
            lineHeight: "20px",
            color: "text.secondary",
          }}
        >
          {parts.map(({ key, node }, i) => (
            <Fragment key={key}>
              {i ? (
                <Box component="span" aria-hidden sx={{ color: "neutral.400" }}>
                  ·
                </Box>
              ) : null}
              {node}
            </Fragment>
          ))}
        </Stack>
      </Box>
      <StatusChip status={row.status} />
      {actions}
    </Paper>
  );
};
